// @ts-nocheck
/**
 * フロア生成モジュール
 * 各階層の選択肢と敵を生成
 * @module twinKey/core/dungeon/floorGenerator
 */

import { seededRandom, shuffleWithSeed } from './variationGenerator';
import { calculateEnemyLevel, calculateBossLevel, calculateStrongEnemyLevel } from './depthCalculator';
import { FLOOR_TYPES } from './data/continents';
import { REST_HP_RECOVERY, STRONG_ENEMY_REWARD_MULTIPLIER } from '../../data/constants';
import { EnemyGenerator } from '../enemy';

// ============================================================
// メイン関数
// ============================================================

// グローバルな EnemyGenerator インスタンス（cardPool が設定されている場合）
let enemyGeneratorInstance = null;

/**
 * 敵生成用の cardPool を設定
 * @param {Object[]} cardPool - 全カードデータ配列
 */
export function setCardPool(cardPool) {
  if (cardPool && cardPool.length > 0) {
    // インスタンスはダンジョンごとに作成するため、ここでは保存しない
    // 代わりに、グローバル変数に cardPool を保存
    setCardPool.cardPool = cardPool;
  }
}

/**
 * フロアの選択肢を生成
 * @param {Object} dungeon - ダンジョン情報
 * @param {number} floorNumber - 現在の階層
 * @param {Object[]} [cardPool] - カードプール（省略時はsetCardPoolで設定したものを使用）
 * @returns {Array} フロア選択肢の配列
 */
export function generateFloorChoices(dungeon, floorNumber, cardPool = null) {
  const seed = dungeon.seed + floorNumber * 1000;
  const random = seededRandom(seed);

  // cardPool を取得（引数優先、次にグローバル設定）
  const effectiveCardPool = cardPool || setCardPool.cardPool || null;

  // EnemyGenerator を取得または作成
  const enemyGenerator = effectiveCardPool
    ? new EnemyGenerator(effectiveCardPool, dungeon.seed, dungeon.maxFloor)
    : null;

  // 深度プールを初期化（シードベースなので毎回同じ結果）
  if (enemyGenerator) {
    enemyGenerator.initializeDepthPools(dungeon);
  }

  // ボス階層は固定
  if (dungeon.bossFloors.includes(floorNumber)) {
    return [{
      type: 'boss',
      name: `${floorNumber}F ボス戦`,
      description: `${floorNumber}Fのボスが待ち受けている`,
      icon: FLOOR_TYPES.boss.icon,
      enemies: enemyGenerator
        ? enemyGenerator.generateFloorEnemies(dungeon, floorNumber, 'boss')
        : generateBossEnemies(dungeon, floorNumber),
      fixed: true,
      rewards: calculateBossRewards(dungeon, floorNumber),
    }];
  }

  // ボス後階層は休憩 or 宝箱
  const previousFloorWasBoss = dungeon.bossFloors.includes(floorNumber - 1);
  if (previousFloorWasBoss) {
    return [
      {
        type: 'rest',
        name: '休憩',
        description: `HP${Math.floor(REST_HP_RECOVERY * 100)}%回復`,
        icon: FLOOR_TYPES.rest.icon,
        effect: { type: 'heal', value: REST_HP_RECOVERY },
      },
      {
        type: 'treasure',
        name: '宝箱',
        description: 'アイテム獲得',
        icon: FLOOR_TYPES.treasure.icon,
        rewards: generateTreasureRewards(dungeon, floorNumber, random),
      },
    ];
  }

  // 最終フロア前は休憩可能
  if (floorNumber === dungeon.maxFloor - 1) {
    return [
      {
        type: 'battle',
        name: '戦闘',
        description: generateEnemyDescription(dungeon, floorNumber, random, enemyGenerator),
        icon: FLOOR_TYPES.battle.icon,
        enemies: enemyGenerator
          ? enemyGenerator.generateFloorEnemies(dungeon, floorNumber, 'normal')
          : generateNormalEnemies(dungeon, floorNumber, random),
        rewards: calculateBattleRewards(dungeon, floorNumber, false),
      },
      {
        type: 'rest',
        name: '休憩',
        description: `HP${Math.floor(REST_HP_RECOVERY * 100)}%回復（最終戦前）`,
        icon: FLOOR_TYPES.rest.icon,
        effect: { type: 'heal', value: REST_HP_RECOVERY },
      },
    ];
  }

  // 通常階層は3つの選択肢
  const choices = [];

  // 必ず戦闘を1つ
  choices.push({
    type: 'battle',
    name: '戦闘',
    description: generateEnemyDescription(dungeon, floorNumber, random, enemyGenerator),
    icon: FLOOR_TYPES.battle.icon,
    enemies: enemyGenerator
      ? enemyGenerator.generateFloorEnemies(dungeon, floorNumber, 'normal')
      : generateNormalEnemies(dungeon, floorNumber, random),
    rewards: calculateBattleRewards(dungeon, floorNumber, false),
  });

  // 残り2つはランダム
  const options = ['treasure', 'event', 'trap', 'strong_enemy'];
  for (let i = 0; i < 2; i++) {
    const idx = Math.floor(random() * options.length);
    const type = options.splice(idx, 1)[0];
    choices.push(generateChoice(type, dungeon, floorNumber, random, enemyGenerator));
  }

  return shuffleWithSeed(choices, seed + 999);
}

/**
 * 選択肢を生成
 * @param {string} type - フロアタイプ
 * @param {Object} dungeon - ダンジョン情報
 * @param {number} floorNumber - 階層
 * @param {Function} random - 乱数関数
 * @param {EnemyGenerator|null} enemyGenerator - 敵生成器
 * @returns {Object} 選択肢
 */
function generateChoice(type, dungeon, floorNumber, random, enemyGenerator = null) {
  switch (type) {
    case 'treasure':
      return {
        type: 'treasure',
        name: '宝箱',
        description: 'アイテム獲得',
        icon: FLOOR_TYPES.treasure.icon,
        rewards: generateTreasureRewards(dungeon, floorNumber, random),
      };

    case 'event':
      return generateEventChoice(dungeon, floorNumber, random);

    case 'trap':
      return generateTrapChoice(dungeon, floorNumber, random);

    case 'strong_enemy':
      return {
        type: 'strong_enemy',
        name: '強敵',
        description: '強い敵、報酬2倍',
        icon: FLOOR_TYPES.strong_enemy.icon,
        enemies: enemyGenerator
          ? enemyGenerator.generateFloorEnemies(dungeon, floorNumber, 'elite')
          : generateStrongEnemies(dungeon, floorNumber, random),
        rewards: calculateBattleRewards(dungeon, floorNumber, true),
      };

    default:
      return {
        type: 'battle',
        name: '戦闘',
        description: '敵×2',
        icon: FLOOR_TYPES.battle.icon,
        enemies: enemyGenerator
          ? enemyGenerator.generateFloorEnemies(dungeon, floorNumber, 'normal')
          : generateNormalEnemies(dungeon, floorNumber, random),
        rewards: calculateBattleRewards(dungeon, floorNumber, false),
      };
  }
}

// ============================================================
// 敵生成
// ============================================================

/**
 * 通常敵を生成
 * @param {Object} dungeon - ダンジョン情報
 * @param {number} floorNumber - 階層
 * @param {Function} random - 乱数関数
 * @returns {Array} 敵配列
 */
export function generateNormalEnemies(dungeon, floorNumber, random) {
  const count = 2 + Math.floor(random() * 2); // 2-3体
  const level = calculateEnemyLevel(dungeon.depth, floorNumber, dungeon.maxFloor);
  const enemies = [];

  for (let i = 0; i < count; i++) {
    enemies.push(createEnemyPlaceholder(dungeon, level, random, i));
  }

  return enemies;
}

/**
 * 強敵を生成
 * @param {Object} dungeon - ダンジョン情報
 * @param {number} floorNumber - 階層
 * @param {Function} random - 乱数関数
 * @returns {Array} 敵配列
 */
export function generateStrongEnemies(dungeon, floorNumber, random) {
  const count = 1 + Math.floor(random() * 2); // 1-2体
  const level = calculateStrongEnemyLevel(dungeon.depth, floorNumber, dungeon.maxFloor);
  const enemies = [];

  for (let i = 0; i < count; i++) {
    enemies.push(createEnemyPlaceholder(dungeon, level, random, i, true));
  }

  return enemies;
}

/**
 * ボス敵を生成
 * @param {Object} dungeon - ダンジョン情報
 * @param {number} floorNumber - 階層
 * @returns {Array} 敵配列
 */
export function generateBossEnemies(dungeon, floorNumber) {
  const random = seededRandom(dungeon.seed + floorNumber * 7777);
  const level = calculateBossLevel(dungeon.depth, floorNumber, dungeon.maxFloor);

  // ボス1体 + 取り巻き0-2体
  const enemies = [];

  // ボス
  enemies.push(createEnemyPlaceholder(dungeon, level, random, 0, false, true));

  // 取り巻き
  const minionCount = Math.floor(random() * 3);
  const minionLevel = calculateEnemyLevel(dungeon.depth, floorNumber, dungeon.maxFloor);
  for (let i = 0; i < minionCount; i++) {
    enemies.push(createEnemyPlaceholder(dungeon, minionLevel, random, i + 1));
  }

  return enemies;
}

/**
 * 敵プレースホルダーを作成
 * 実際のカードデータはバトル開始時にロード
 * @param {Object} dungeon - ダンジョン情報
 * @param {number} level - レベル
 * @param {Function} random - 乱数関数
 * @param {number} index - インデックス
 * @param {boolean} isStrong - 強敵フラグ
 * @param {boolean} isBoss - ボスフラグ
 * @returns {Object} 敵プレースホルダー
 */
function createEnemyPlaceholder(dungeon, level, random, index, isStrong = false, isBoss = false) {
  return {
    id: `enemy_${index}`,
    level,
    attribute: dungeon.enemyBias,
    isStrong,
    isBoss,
    // 実際のカードはバトル開始時に決定
    cardId: null,
    name: isBoss ? 'ボス' : (isStrong ? '強敵' : '敵'),
  };
}

/**
 * 敵の説明文を生成
 * @param {Object} dungeon - ダンジョン情報
 * @param {number} floorNumber - 階層
 * @param {Function} random - 乱数関数
 * @param {EnemyGenerator|null} enemyGenerator - 敵生成器
 * @returns {string} 説明文
 */
function generateEnemyDescription(dungeon, floorNumber, random, enemyGenerator = null) {
  const count = 2 + Math.floor(random() * 2);
  const level = calculateEnemyLevel(dungeon.depth, floorNumber, dungeon.maxFloor);

  // EnemyGenerator がある場合はレベル計算を使用
  if (enemyGenerator) {
    const calcLevel = enemyGenerator.levelCalculator.calculateLevel(floorNumber, 'normal');
    return `敵×${count}（Lv.${calcLevel}）`;
  }

  return `敵×${count}（Lv.${level}）`;
}

// ============================================================
// イベント・罠
// ============================================================

const EVENTS = [
  { id: 'heal_fountain', name: '癒しの泉', description: 'HP20%回復', effect: { type: 'heal', value: 0.2 } },
  { id: 'mysterious_merchant', name: '謎の商人', description: 'アイテム購入可能', effect: { type: 'shop' } },
  { id: 'treasure_guardian', name: '宝の守護者', description: '戦闘 or 撤退', effect: { type: 'optional_battle' } },
  { id: 'spirit_blessing', name: '精霊の祝福', description: '次の戦闘で攻撃力+20%', effect: { type: 'buff', stat: 'attack', value: 0.2, duration: 1 } },
  { id: 'ancient_inscription', name: '古代の碑文', description: '経験値獲得', effect: { type: 'exp', value: 100 } },
];

const TRAPS = [
  { id: 'poison_gas', name: '毒ガス', description: 'HP10%ダメージ', effect: { type: 'damage', value: 0.1 } },
  { id: 'pitfall', name: '落とし穴', description: 'HP15%ダメージ、1体行動不能', effect: { type: 'damage_stun', value: 0.15 } },
  { id: 'spike_trap', name: 'トゲトラップ', description: 'HP5%ダメージ', effect: { type: 'damage', value: 0.05 } },
  { id: 'curse_trap', name: '呪いの罠', description: '次の戦闘で防御-20%', effect: { type: 'debuff', stat: 'defense', value: -0.2, duration: 1 } },
  { id: 'teleport_trap', name: '転送罠', description: '1フロア戻される', effect: { type: 'teleport', value: -1 } },
];

/**
 * イベント選択肢を生成
 * @param {Object} dungeon - ダンジョン情報
 * @param {number} floorNumber - 階層
 * @param {Function} random - 乱数関数
 * @returns {Object} イベント選択肢
 */
function generateEventChoice(dungeon, floorNumber, random) {
  const event = EVENTS[Math.floor(random() * EVENTS.length)];
  return {
    type: 'event',
    name: event.name,
    description: event.description,
    icon: FLOOR_TYPES.event.icon,
    effect: event.effect,
    eventId: event.id,
  };
}

/**
 * 罠選択肢を生成
 * @param {Object} dungeon - ダンジョン情報
 * @param {number} floorNumber - 階層
 * @param {Function} random - 乱数関数
 * @returns {Object} 罠選択肢
 */
function generateTrapChoice(dungeon, floorNumber, random) {
  const trap = TRAPS[Math.floor(random() * TRAPS.length)];
  return {
    type: 'trap',
    name: '危険な道',
    description: '罠があるかも...?',
    icon: FLOOR_TYPES.trap.icon,
    actualTrap: trap,
    // 実際の罠は選択後に判明
    hidden: true,
  };
}

// ============================================================
// 報酬計算
// ============================================================

/**
 * 戦闘報酬を計算
 * @param {Object} dungeon - ダンジョン情報
 * @param {number} floorNumber - 階層
 * @param {boolean} isStrong - 強敵かどうか
 * @returns {Object} 報酬情報
 */
function calculateBattleRewards(dungeon, floorNumber, isStrong) {
  const baseGold = 100 + floorNumber * 10;
  const baseExp = 50 + floorNumber * 5;

  const depthMultiplier = dungeon.rewardBonus?.multiplier || 1.0;
  const strongMultiplier = isStrong ? STRONG_ENEMY_REWARD_MULTIPLIER : 1.0;

  return {
    gold: Math.floor(baseGold * depthMultiplier * strongMultiplier),
    exp: Math.floor(baseExp * depthMultiplier * strongMultiplier),
    itemChance: isStrong ? 0.5 : 0.2,
  };
}

/**
 * ボス報酬を計算
 * @param {Object} dungeon - ダンジョン情報
 * @param {number} floorNumber - 階層
 * @returns {Object} 報酬情報
 */
function calculateBossRewards(dungeon, floorNumber) {
  const baseGold = 500 + floorNumber * 20;
  const baseExp = 200 + floorNumber * 10;
  const depthMultiplier = dungeon.rewardBonus?.multiplier || 1.0;

  return {
    gold: Math.floor(baseGold * depthMultiplier),
    exp: Math.floor(baseExp * depthMultiplier),
    itemChance: 1.0, // ボスは必ずアイテムドロップ
    guaranteedRarity: floorNumber >= 50 ? 'R' : 'UC',
  };
}

/**
 * 宝箱報酬を生成
 * @param {Object} dungeon - ダンジョン情報
 * @param {number} floorNumber - 階層
 * @param {Function} random - 乱数関数
 * @returns {Object} 報酬情報
 */
function generateTreasureRewards(dungeon, floorNumber, random) {
  const baseGold = 50 + floorNumber * 5;

  return {
    gold: Math.floor(baseGold * (0.8 + random() * 0.4)),
    itemChance: 0.7,
    possibleRarities: floorNumber >= 50 ? ['UC', 'R', 'SR'] : ['C', 'UC', 'R'],
  };
}

// ============================================================
// エクスポート
// ============================================================

export {
  EVENTS,
  TRAPS,
};
